import React, { useEffect, useState } from "react";
import room1 from "../../src/assets/room2.avif";
import RetailerSidebar from "../components/retailer/RetailerSidebar";
import { getCompanyLogo, uploadCompanyLogo } from "../services/api";

export default function PreviewSettingPage() {
  const [profileLogo, setProfileLogo] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [fileName, setFileName] = useState("No file chosen");
  const [logoPosition, setLogoPosition] = useState("Bottom Right");
  const [logoSize, setLogoSize] = useState(140);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingLogo, setIsLoadingLogo] = useState(false);

  const [customText, setCustomText] = useState("");
  const [textPosition, setTextPosition] = useState("Top Left");
  const [fontSize, setFontSize] = useState(18);

  const defaultProfileLogo =
    "https://via.placeholder.com/120x40?text=YOUR+COMPANY+LOGO+HERE";
  const token = localStorage.getItem("retailer_token");
  // Replace this placeholder URL with your room image path/URL later

  useEffect(() => {
    async function fetchLogo() {
      try {
        setIsLoadingLogo(true);

        const data = await getCompanyLogo(token);
        // Expecting backend response to contain logo_url or logo key
        const fetchedUrl = data.logo_url || data.logo || data.url;
        if (fetchedUrl) {
          setProfileLogo(fetchedUrl);
        }
      } catch (error) {
        console.error("Failed to load company logo:", error);
      } finally {
        setIsLoadingLogo(false);
      }
    }

    if (token) fetchLogo();
  }, []);

  const roomPlaceholderImage = room1;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 1. Immediately create a local preview URL so the logo appears on the room image right away
    const localPreviewUrl = URL.createObjectURL(file);
    setLogoUrl(localPreviewUrl);
    setFileName(file.name);
    setIsUploading(true);

    try {
      // 2. Upload file to backend
      const response = await uploadCompanyLogo(file, token);
      const uploadedUrl = response.logo_url || response.logo || response.url;

      // 3. Replace local URL with permanent server URL once uploaded
      if (uploadedUrl) {
        setLogoUrl(uploadedUrl);
        setProfileLogo(uploadedUrl); // update profile logo reference
      }
    } catch (error) {
      console.error("Logo upload failed:", error);
      alert(error.message || "Failed to upload company logo");
      // Revert states on error
      setLogoUrl(null);
      setFileName("No file chosen");
    } finally {
      setIsUploading(false);
    }
  };

  const handleUseProfileLogo = () => {
    if (!profileLogo) {
      return alert("No profile logo found. Please upload one first.");
    }
    setLogoUrl(profileLogo);
    setFileName("Profile Logo Selected");
  };

  const handleRemoveLogo = () => {
    setLogoUrl(null);
    setFileName("No file chosen");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const settingsPayload = {
      logoUrl,
      logoPosition,
      logoSize: Number(logoSize),
      customText,
      textPosition,
      fontSize: Number(fontSize),
    };
    console.log("Saving Preview Settings:", settingsPayload);
  };

  // Utility to align overlays over the room preview
  const getPositionClasses = (position) => {
    switch (position) {
      case "Top Left":
        return "top-3 left-3";
      case "Top Right":
        return "top-3 right-3";
      case "Bottom Left":
        return "bottom-3 left-3";
      case "Bottom Right":
      default:
        return "bottom-3 right-3";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <RetailerSidebar />

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6 w-full">
        <div className="w-full bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">
            Preview Setting
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section: Show Profile Logo Option ONLY if profile logo is available */}
            {profileLogo ? (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">
                  Your Profile Logo
                </label>
                <div className="flex items-center gap-3">
                  <div className="border border-gray-300 p-2 rounded bg-white min-w-[140px] h-14 flex items-center justify-center">
                    {isLoadingLogo ? (
                      <span className="text-xs text-gray-400">Loading...</span>
                    ) : (
                      <img
                        src={profileLogo}
                        alt="Your Profile Logo"
                        className="max-h-10 object-contain"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleUseProfileLogo}
                    disabled={isLoadingLogo}
                    className="px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-600 rounded hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Use Profile Logo
                  </button>
                </div>
              </div>
            ) : (
              /* Section: Show Choose Logo File Input ONLY if profile logo is not available */
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
  <div className="md:col-span-5">
    <label className="block text-xs font-semibold text-gray-600 mb-1">
      Choose Logo Image
    </label>
    <div className="flex border border-gray-300 rounded overflow-hidden">
      <label className="bg-gray-100 text-gray-700 px-3 py-2 text-xs border-r border-gray-300 cursor-pointer hover:bg-gray-200">
        {isUploading ? "Uploading..." : "Choose file"}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={isUploading}
          className="hidden"
        />
      </label>
      <span className="px-3 py-2 text-xs text-gray-500 truncate flex-1 bg-white">
        {fileName}
      </span>
    </div>
  </div>

  <div className="md:col-span-3">
    <label className="block text-xs font-semibold text-gray-600 mb-1">
      Logo Position
    </label>
    <select
      value={logoPosition}
      onChange={(e) => setLogoPosition(e.target.value)}
      className="w-full border border-gray-300 rounded px-3 py-2 text-xs bg-white focus:outline-none focus:border-indigo-500"
    >
      <option>Bottom Right</option>
      <option>Bottom Left</option>
      <option>Top Right</option>
      <option>Top Left</option>
    </select>
  </div>

  <div className="md:col-span-2">
    <label className="block text-xs font-semibold text-gray-600 mb-1">
      Logo Size
    </label>
    <input
      type="number"
      value={logoSize}
      onChange={(e) => setLogoSize(e.target.value)}
      className="w-full border border-gray-300 rounded px-3 py-2 text-xs focus:outline-none focus:border-indigo-500"
    />
  </div>

  <div className="md:col-span-2">
    <button
      type="button"
      onClick={handleRemoveLogo}
      className="w-full py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors"
    >
      Remove Logo
    </button>
  </div>
</div>
            )}

            {/* Controls Row: Logo Position, Size, and Text Settings */}

            {/* Controls Row 2: Text Settings */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <div className="md:col-span-5">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Enter Custom Text
                </label>
                <input
                  type="text"
                  placeholder="Enter text here"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-xs focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="md:col-span-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Text Position
                </label>
                <select
                  value={textPosition}
                  onChange={(e) => setTextPosition(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-xs bg-white focus:outline-none focus:border-indigo-500"
                >
                  <option>Top Left</option>
                  <option>Top Right</option>
                  <option>Bottom Left</option>
                  <option>Bottom Right</option>
                </select>
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Font Size
                </label>
                <input
                  type="number"
                  value={fontSize}
                  onChange={(e) => setFontSize(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-xs focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Section: Image Preview */}
            <div className="pt-2">
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                Image Preview
              </label>
              <div className="relative inline-block border border-gray-200 rounded overflow-hidden max-w-2xl bg-gray-100">
                <img
                  src={roomPlaceholderImage}
                  alt="Room Preview"
                  className="w-full h-auto object-cover block"
                />

                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt="Logo Overlay"
                    style={{ width: `${logoSize}px` }}
                    className={`absolute ${getPositionClasses(logoPosition)} object-contain transition-all pointer-events-none`}
                  />
                )}

                {customText && (
                  <div
                    style={{ fontSize: `${fontSize}px` }}
                    className={`absolute ${getPositionClasses(textPosition)} text-gray-800 font-semibold bg-white/60 px-2 py-0.5 rounded transition-all pointer-events-none`}
                  >
                    {customText}
                  </div>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isUploading}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded shadow-sm transition-colors disabled:opacity-50"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
